import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalActions, ModalContent, ModalTitleInverse } from '@c2fo/liquidity';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import localeRateToDomain from '@/utils/localeRateToDomain';
import { useLocaleFormat } from '@/utils/useLocaleFormat';
import { getNormalizedRate } from '@/utils/validateRate';
import { RateType, useOfferBuilderContext } from '../../OfferBuilderContext';
import { useValidateRate, ValidateRateResult, buildTakerMarketKey } from '../../utils';
import NameYourRateInput from './NameYourRateInput';
import NameYourRateSlider from './NameYourRateSlider';

interface NameYourRateModalProps {
  takerMarket: TakerMarket;
  option?: {
    rateType: RateType;
    suggestedRate: number | undefined;
    suggestedRateEnd: number | undefined;
    suggestedRateStart: number | undefined;
  };
  open: boolean;
  onClose: () => void;
}

const NameYourRateModal = ({ takerMarket, option, open, onClose }: NameYourRateModalProps) => {
  const { t } = useTranslation();
  const {
    dispatch,
    state: { frequency, rateType: rateTypeFromContext, workingTakerMarkets },
  } = useOfferBuilderContext();
  const { trackEnterpriseEvent } = useReporting();
  const { asNumber } = useLocaleFormat();
  const { validateRate } = useValidateRate();
  const rateInputRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef(false);

  const marketKey = buildTakerMarketKey(takerMarket);
  const existingMarketRate = workingTakerMarkets[marketKey];
  const rateToUse = existingMarketRate?.rate ? asNumber({ value: existingMarketRate.rate, decimals: 2 }) : '';
  const rateTypeToUse = existingMarketRate?.rateType ?? option?.rateType ?? rateTypeFromContext ?? 'APR';

  const [internalRate, setInternalRate] = useState(rateToUse);
  const [internalRateType, setInternalRateType] = useState<RateType>(rateTypeToUse);
  const [rateError, setRateError] = useState<ValidateRateResult | null>(null);
  const [rateTypeError, setRateTypeError] = useState<string | null>(null);
  const [lastInputMethod, setLastInputMethod] = useState<'input' | 'slider' | null>(null);
  const [hasSliderInteraction, setHasSliderInteraction] = useState(false);

  // initialize state when modal opens
  useEffect(() => {
    if (open && !initializedRef.current) {
      const newRate = rateToUse;
      const newRateType = rateTypeToUse;

      setInternalRate(newRate);
      setInternalRateType(newRateType);
      setLastInputMethod(null);
      setHasSliderInteraction(false);
      initializedRef.current = true;
    } else if (!open) {
      // reset initialization flag when modal closes
      initializedRef.current = false;
    }
  }, [open, asNumber, rateToUse, rateTypeToUse]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        if (rateInputRef.current) {
          rateInputRef.current.focus();
        }
      });
    }
  }, [open]);

  const isValid = useMemo(() => {
    const domainRate = localeRateToDomain(internalRate) ?? '';
    const hasRate = internalRate.trim() !== '';
    const hasRateType = !!internalRateType;

    if (hasRate && hasRateType && domainRate) {
      const validationResult = validateRate(domainRate, internalRateType);
      return validationResult.valid;
    }

    return hasRate && hasRateType;
  }, [internalRate, internalRateType, validateRate]);

  const handleRateChange = (value: string) => {
    setInternalRate(value);
    setRateError(null);
    setLastInputMethod('input');
  };

  const handleRateBlur = () => {
    const domainRate = localeRateToDomain(internalRate) ?? '';

    if (domainRate && internalRateType) {
      const validationResult = validateRate(domainRate, internalRateType);

      if (!validationResult.valid) {
        setRateError(validationResult);
      }
    }
  };

  const handleRateTypeChange = (value: RateType) => {
    setInternalRateType(value);
    setRateTypeError(null);
    setInternalRate('');
  };

  const handleSliderChange = (value: number) => {
    const formattedValue = asNumber({ value, decimals: 2 });
    setInternalRate(formattedValue);
    setRateError(null);
    setLastInputMethod('slider');
    setHasSliderInteraction(true);
  };

  const sliderValue = useMemo(() => {
    if (internalRate.trim()) {
      const domainRate = localeRateToDomain(internalRate);
      if (domainRate) {
        return getNormalizedRate(domainRate);
      }
    }

    return option?.suggestedRate ?? 0;
  }, [internalRate, option?.suggestedRate]);

  const { sliderSuggested } = useMemo(() => {
    if (!option?.suggestedRateStart || !option?.suggestedRateEnd || !option?.suggestedRate) {
      return {
        guidanceMessage: '',
        sliderSuggested: { rate: 0, lowSlider: 0, highSlider: 0 },
      };
    }

    const startRate = asNumber({
      value: option.suggestedRateStart,
      decimals: 2,
    });
    const endRate = asNumber({
      value: option.suggestedRateEnd,
      decimals: 2,
    });

    return {
      guidanceMessage:
        internalRateType === 'APR'
          ? t('builder.guidanceMessageApr', { startRate, endRate })
          : t('builder.guidanceMessageDisc', { startRate, endRate }),
      sliderSuggested: {
        rate: option.suggestedRate,
        lowSlider: option.suggestedRateStart,
        highSlider: option.suggestedRateEnd,
      },
    };
  }, [asNumber, option, internalRateType, t]);

  const handleSave = () => {
    if (!isValid || !internalRate || !internalRateType) {
      return;
    }

    const domainRate = localeRateToDomain(internalRate) ?? '';
    if (!domainRate) {
      return;
    }

    const normalizedRate = getNormalizedRate(domainRate);

    trackEnterpriseEvent('offer-builder::rate::set', {
      rate: normalizedRate,
      rateType: internalRateType,
      pricingStrategy: 'nameYourRate',
      frequency,
      lastInputMethod,
      hasSliderInteraction,
      marketUuid: takerMarket.marketUuid,
      takerDivisionUuid: takerMarket.takerDivisionUuid,
      makerOrganizationUuid: takerMarket.makerOrganizationUuid,
    });

    dispatch({
      type: 'UPDATE_WORKING_TAKER_MARKETS_RATE',
      payload: [
        {
          takerMarketKey: marketKey,
          rateInfo: {
            rate: normalizedRate,
            rateType: internalRateType,
            pricingRateUuid: existingMarketRate?.pricingRateUuid,
            suggestedRate: option?.suggestedRate,
            suggestRateType: option?.rateType,
            customRate: normalizedRate,
            customRateType: internalRateType,
            pricingStrategy: 'nameYourRate',
            isIndividuallyOverridden: true,
          },
        },
      ],
    });

    onClose();
  };

  const handleClose = () => {
    if (!!lastInputMethod || !!hasSliderInteraction) {
      trackEnterpriseEvent('offer-builder::name-your-rate-modal::last-interaction', {
        lastInputMethod,
        hasSliderInteraction,
        marketUuid: takerMarket.marketUuid,
        takerDivisionUuid: takerMarket.takerDivisionUuid,
        makerOrganizationUuid: takerMarket.makerOrganizationUuid,
      });
    }

    onClose();
  };

  return (
    <Modal onClose={handleClose} open={open} size="sm">
      <ModalTitleInverse>
        <span className="flex flex-col">
          <span>{t('core.nameYourRate')}</span>
          <span className="text-sm font-normal text-text-secondary">
            {frequency === 'oneTime' ? t('builder.oneTimeOffer') : t('builder.recurringOffer')}
          </span>
        </span>
      </ModalTitleInverse>
      <ModalContent className="pt-0">
        {option ? (
          <div className="space-y-4">
            <div>{t('builder.enterARateForYourOffer')}</div>
            <div className="w-72">
              <NameYourRateInput
                internalRate={internalRate}
                internalRateType={internalRateType}
                rateError={rateError}
                rateTypeError={rateTypeError}
                onRateChange={handleRateChange}
                onRateBlur={handleRateBlur}
                onRateTypeChange={handleRateTypeChange}
                rateInputRef={rateInputRef}
              />
              <div className="pt-8">
                <NameYourRateSlider value={sliderValue} onChange={handleSliderChange} suggested={sliderSuggested} />
              </div>
            </div>
            <div className="text-sm text-text-secondary">{t('builder.offerGuidanceNote')}</div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>{t('builder.enterARateForYourOffer')}</div>
            <div className="w-72">
              <NameYourRateInput
                internalRate={internalRate}
                internalRateType={internalRateType}
                rateError={rateError}
                rateTypeError={rateTypeError}
                onRateChange={handleRateChange}
                onRateBlur={handleRateBlur}
                onRateTypeChange={handleRateTypeChange}
                rateInputRef={rateInputRef}
              />
            </div>
          </div>
        )}
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={handleClose}>
          {t('core.cancel')}
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!isValid}>
          {t('core.save')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default NameYourRateModal;
